import React, { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { Modal, Form, Spin, Button, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { getAdminTab } from '../../graphql/queries';
import { createTab, updateTab, deleteTab } from '../../graphql/mutation';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import TabForm from './TabForm';

const buildInput = (values) => ({
  name: values.name,
  isActive: values.isActive !== undefined ? values.isActive : true,
  contentType: values.contentType,
  additionMethod: values.additionMethod,
  // Ordering applies regardless of additionMethod — not gated by RULE_BASED.
  sortMode: values.sortMode || 'CUSTOM',
  sortField: values.sortMode === 'DYNAMIC' ? values.sortField : null,
  sortDirection: values.sortMode === 'DYNAMIC' ? values.sortDirection : null,
  usageWindowDays:
    values.sortMode === 'DYNAMIC' && values.sortField === 'USAGE_COUNT' ? values.usageWindowDays || null : null,
  // Filters only apply to RULE_BASED tabs (a MANUAL tab's membership is the
  // explicit hand-picked list — nothing to filter).
  typeFilter: values.additionMethod === 'RULE_BASED' ? values.typeFilter || null : null,
  limit: values.additionMethod === 'RULE_BASED' ? values.limit || null : null,
  maxPerCategory:
    values.additionMethod === 'RULE_BASED' && values.contentType === 'TOOL' ? values.maxPerCategory || null : null,
  // Shuffle was removed from the rule UI — always send false.
  shuffleResults: false,
});

// Shared modal for both creating a new Tab and editing an existing one.
// tabId === null -> create mode, otherwise fetches and edits that tab.
const TabModal = ({ open, tabId, onClose, onSaved, onDeleted }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const isEdit = !!tabId;

  const [fetchTab, { data, loading: queryLoading }] = useLazyQuery(getAdminTab, {
    fetchPolicy: 'network-only',
  });

  const [createTabMutation] = useMutation(createTab);
  const [updateTabMutation] = useMutation(updateTab);
  const [deleteTabMutation] = useMutation(deleteTab);

  useEffect(() => {
    if (open && isEdit) {
      fetchTab({ variables: { id: tabId } });
    }
    if (open && !isEdit) {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tabId]);

  const tab = data?.getAdminTab;

  const initialValues = isEdit && tab
    ? {
        name: tab.name,
        isActive: tab.isActive,
        contentType: tab.contentType,
        additionMethod: tab.additionMethod,
        sortMode: tab.sortMode,
        sortField: tab.sortField,
        sortDirection: tab.sortDirection || 'ASC',
        usageWindowDays: tab.usageWindowDays,
        typeFilter: tab.typeFilter,
        limit: tab.limit,
        maxPerCategory: tab.maxPerCategory,
      }
    : undefined;

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const input = buildInput(values);

      if (isEdit) {
        await updateTabMutation({ variables: { id: tabId, input } });
        showSnackBar('success', 'Success', 'Tab updated successfully');
      } else {
        await createTabMutation({ variables: { input } });
        showSnackBar('success', 'Success', 'Tab created successfully');
      }

      onSaved();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to save tab');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Tab',
      content: 'Are you sure you want to delete this tab? This will also delete all of its items.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteTabMutation({ variables: { id: tabId } });
          showSnackBar('success', 'Success', 'Tab deleted');
          onDeleted();
        } catch (error) {
          showSnackBar('error', 'Error', error.message || 'Failed to delete tab');
        }
      },
    });
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Tab' : 'Create Tab'}
      onCancel={onClose}
      footer={null}
      width={640}
      centered
      destroyOnClose
    >
      {isEdit && (queryLoading || !tab) ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <TabForm
          form={form}
          initialValues={initialValues}
          onFinish={handleSubmit}
          submitLabel={isEdit ? 'Update Tab' : 'Create Tab'}
          loading={loading}
          extraActions={
            isEdit ? (
              <Space>
                <Button onClick={onClose}>Cancel</Button>
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  Delete Tab
                </Button>
              </Space>
            ) : (
              <Button onClick={onClose}>Cancel</Button>
            )
          }
        />
      )}
    </Modal>
  );
};

export default TabModal;
